import Reveal from "@/components/Reveal";

const PORTRAIT = "/simran-kaur.jpg";

const chapters = [
  {
    n: "01",
    title: "The therapeutic relationship",
    text: "At the heart of my work is the therapeutic relationship. I believe healing begins when we feel safe enough to be fully ourselves \u2014 heard, understood and accepted without judgement.",
  },
  {
    n: "02",
    title: "A containing space",
    text: "I offer a warm, relational and containing space where you can slow down, explore your inner world, and make sense of your experiences at a pace that feels right for you. Together, we create a relationship where difficult emotions can be held with care, explored with curiosity and, over time, become more manageable and meaningful.",
  },
  {
    n: "03",
    title: "An integrative practice",
    text: "With over six years of clinical experience, my practice is integrative \u2014 rooted primarily in relational and psychodynamic psychotherapy, while drawing on somatic awareness and creative approaches where they enrich the process. Alongside listening to your journey, I pay close attention to what is communicated through the body, recognising that our bodies often hold experiences that words alone cannot express. My work is further enriched through training in Transactional Analysis (TA 101) and Psychodrama.",
  },
  {
    n: "04",
    title: "A cross-cultural lens",
    text: "Having trained and practised in India before establishing my practice in the UK, I bring a deep appreciation of diversity and the cultural contexts that shape our identities. I welcome clients from all backgrounds and remain mindful of how culture, family, migration, belonging and difference influence our emotional lives. Healing is not about becoming someone different \u2014 it is about finding the freedom to come more fully yourself.",
  },
];

const onSpotlightMove = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mouse-x", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--mouse-y", `${e.clientY - r.top}px`);
};

const Manifesto = () => (
  <section id="about" className="py-20 md:py-32" data-testid="about-section">
    <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-12 px-6 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12">
      <div className="lg:col-span-5 lg:sticky lg:top-28">
        <Reveal>
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.22em] text-[#8C827A]">About the practice</p>
          <h2 className="font-serif text-3xl font-normal tracking-tight text-[#2C2825] sm:text-4xl lg:text-5xl">
            A philosophy in<br />
            <span className="italic text-[#C46B4E]">four movements</span>
          </h2>
          <div
            className="spotlight-frame mt-10 overflow-hidden rounded-2xl border border-[#E3DCD3] shadow-lg"
            onMouseMove={onSpotlightMove}
            data-testid="about-portrait-frame"
          >
            <img
              src={PORTRAIT}
              alt="Simran Kaur, psychotherapist, in a softly lit consultation space"
              className="aspect-[4/5] w-full object-cover"
            />
          </div>
          <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#8C827A]">
            Simran Kaur &middot; Integrative Psychotherapist &middot; NCPS 7249
          </p>
        </Reveal>
      </div>

      <div className="lg:col-span-7">
        {chapters.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.08}>
            <article
              data-testid={`manifesto-chapter-${i + 1}`}
              className="group border-t border-[#E3DCD3] py-10 first:border-t-0 first:pt-0 lg:py-12"
            >
              <div className="flex items-baseline gap-6">
                <span className="font-mono text-sm text-[#C46B4E] transition-transform duration-500 group-hover:-translate-y-1">
                  {c.n}
                </span>
                <div>
                  <h3 className="mb-4 font-serif text-xl font-medium text-[#3D3733] sm:text-2xl">{c.title}</h3>
                  <p className="max-w-2xl text-base font-light leading-relaxed text-[#5E564F] sm:text-lg">{c.text}</p>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Manifesto;
